<template>
  <div style="max-width: 600px" class="mx-auto pa-4">
    <v-card>
      <v-card-title>
        <v-btn @click="goToPreviousMonth" small icon>
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <div class="mx-2">
          <span class="text-h6">{{ displayMonthName }}</span>
          <div v-if="selectedCategoryName" class="text-caption grey--text">
            Filtered by: {{ selectedCategoryName }}
          </div>
        </div>
        <v-btn @click="goToNextMonth" small icon :disabled="isCurrentMonth">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          v-if="selectedCategoryId"
          @click="clearFilter"
          small
          text
          color="error"
          class="mr-2"
        >
          Clear Filter
        </v-btn>
        <v-btn
          @click="goToCurrentMonth"
          small
          text
          color="primary"
          class="mr-2"
        >
          Current
        </v-btn>
        <v-btn @click="getTransactions" small icon :loading="loading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <div v-if="monthlyTransactions.length > 0">
          <!-- Debit Transactions -->
          <v-card class="mb-4" v-if="debitTransactions.length > 0">
            <v-card-title class="pb-2">
              <v-icon color="red" class="mr-2">mdi-minus-circle</v-icon>
              <span class="text-h6 red--text">Debit Transactions</span>
              <v-spacer></v-spacer>
              <v-chip color="red" text-color="white" small>
                {{ formatAmount(totalDebit) }}
              </v-chip>
            </v-card-title>

            <!-- Category Totals for Debit -->
            <v-card-text v-if="debitCategoryTotals.length > 0" class="pb-2">
              <div class="text-subtitle2 mb-2">Totals by Category:</div>

              <!-- Chart and Chips Toggle -->
              <v-row class="mb-3">
                <v-col cols="12">
                  <v-btn-toggle v-model="debitViewMode" mandatory dense>
                    <v-btn small value="chips">
                      <v-icon small>mdi-tag-multiple</v-icon>
                      Chips
                    </v-btn>
                    <v-btn small value="chart">
                      <v-icon small>mdi-chart-pie</v-icon>
                      Chart
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <!-- Chips View -->
              <v-row v-if="debitViewMode === 'chips'">
                <v-col
                  v-for="categoryTotal in debitCategoryTotals"
                  :key="categoryTotal.name"
                  cols="6"
                  sm="4"
                  md="3"
                >
                  <v-chip
                    :color="getCategoryColor(categoryTotal.name)"
                    small
                    outlined
                    clickable
                    class="mr-1 mb-1 cursor-pointer"
                    @click="filterByCategory(categoryTotal.name)"
                  >
                    {{ categoryTotal.name }}:
                    {{ formatAmount(categoryTotal.total) }}
                  </v-chip>
                </v-col>
              </v-row>

              <!-- Chart View -->
              <div v-if="debitViewMode === 'chart'" class="chart-container">
                <canvas ref="debitChart" width="400" height="200"></canvas>
              </div>
            </v-card-text>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Date</th>
                    <th class="text-left">Description</th>
                    <th class="text-left">Category</th>
                    <th class="text-right">Amount</th>
                    <th class="text-center">Processed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="transaction in debitTransactions"
                    :key="'debit-' + transaction.id"
                    @click="viewTransaction(transaction)"
                    class="cursor-pointer"
                  >
                    <td>{{ formatDate(transaction.created_at) }}</td>
                    <td>{{ transaction.description || 'No description' }}</td>
                    <td>
                      <v-chip
                        v-if="transaction.category && transaction.category.name"
                        :color="getCategoryColor(transaction.category.name)"
                        small
                        outlined
                      >
                        {{ transaction.category.name }}
                      </v-chip>
                      <span
                        v-if="transaction.category == null"
                        class="grey--text"
                        >Uncategorized</span
                      >
                    </td>
                    <td class="text-right red--text">
                      {{ formatAmount(Math.abs(transaction.amount)) }}
                    </td>
                    <td class="text-center">
                      <v-chip
                        :color="getProcessedColor(transaction.processed)"
                        :text-color="
                          getProcessedTextColor(transaction.processed)
                        "
                        small
                      >
                        {{ getProcessedText(transaction.processed) }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <!-- Credit Transactions -->
          <v-card class="mb-4" v-if="creditTransactions.length > 0">
            <v-card-title class="pb-2">
              <v-icon color="green" class="mr-2">mdi-plus-circle</v-icon>
              <span class="text-h6 green--text">Credit Transactions</span>
              <v-spacer></v-spacer>
              <v-chip color="green" text-color="white" small>
                {{ formatAmount(totalCredit) }}
              </v-chip>
            </v-card-title>

            <!-- Category Totals for Credit -->
            <v-card-text v-if="creditCategoryTotals.length > 0" class="pb-2">
              <div class="text-subtitle2 mb-2">Totals by Category:</div>

              <!-- Chart and Chips Toggle -->
              <v-row class="mb-3">
                <v-col cols="12">
                  <v-btn-toggle v-model="creditViewMode" mandatory dense>
                    <v-btn small value="chips">
                      <v-icon small>mdi-tag-multiple</v-icon>
                      Chips
                    </v-btn>
                    <v-btn small value="chart">
                      <v-icon small>mdi-chart-pie</v-icon>
                      Chart
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
              </v-row>

              <!-- Chips View -->
              <v-row v-if="creditViewMode === 'chips'">
                <v-col
                  v-for="categoryTotal in creditCategoryTotals"
                  :key="categoryTotal.name"
                  cols="6"
                  sm="4"
                  md="3"
                >
                  <v-chip
                    :color="getCategoryColor(categoryTotal.name)"
                    small
                    outlined
                    clickable
                    class="mr-1 mb-1 cursor-pointer"
                    @click="filterByCategory(categoryTotal.name)"
                  >
                    {{ categoryTotal.name }}:
                    {{ formatAmount(categoryTotal.total) }}
                  </v-chip>
                </v-col>
              </v-row>

              <!-- Chart View -->
              <div v-if="creditViewMode === 'chart'" class="chart-container">
                <canvas ref="creditChart" width="400" height="200"></canvas>
              </div>
            </v-card-text>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Date</th>
                    <th class="text-left">Description</th>
                    <th class="text-left">Category</th>
                    <th class="text-right">Amount</th>
                    <th class="text-center">Processed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="transaction in creditTransactions"
                    :key="'credit-' + transaction.id"
                    @click="viewTransaction(transaction)"
                    class="cursor-pointer"
                  >
                    <td>{{ formatDate(transaction.created_at) }}</td>
                    <td>{{ transaction.description || 'No description' }}</td>
                    <td>
                      <v-chip
                        v-if="transaction.category && transaction.category.name"
                        :color="getCategoryColor(transaction.category.name)"
                        small
                        outlined
                      >
                        {{ transaction.category.name }}
                      </v-chip>
                      <span
                        v-if="transaction.category == null"
                        class="grey--text"
                        >Uncategorized</span
                      >
                    </td>
                    <td class="text-right green--text">
                      {{ formatAmount(transaction.amount) }}
                    </td>
                    <td class="text-center">
                      <v-chip
                        :color="getProcessedColor(transaction.processed)"
                        :text-color="
                          getProcessedTextColor(transaction.processed)
                        "
                        small
                      >
                        {{ getProcessedText(transaction.processed) }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>

          <v-divider class="my-4"></v-divider>

          <div class="d-flex justify-space-between">
            <div>
              <div class="text-subtitle2">Total Income:</div>
              <div class="text-h6 green--text">
                {{ formatAmount(totalIncome) }}
              </div>
            </div>
            <div>
              <div class="text-subtitle2">Total Expenses:</div>
              <div class="text-h6 red--text">
                {{ formatAmount(totalExpenses) }}
              </div>
            </div>
            <div>
              <div class="text-subtitle2">Net:</div>
              <div
                class="text-h6"
                :class="netAmount >= 0 ? 'green--text' : 'red--text'"
              >
                {{ formatAmount(netAmount) }}
              </div>
            </div>
          </div>
        </div>

        <div v-else-if="loading" class="text-center py-4">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
          <div class="mt-2">Loading transactions...</div>
        </div>

        <div v-else class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-receipt</v-icon>
          <div class="text-h6 grey--text mt-2">No transactions found</div>
          <div class="text-body-2 grey--text">
            No transactions for {{ displayMonthName }}
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Transaction Edit Dialog -->
    <v-dialog v-model="dialog" persistent max-width="600px" v-if="dialog">
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span class="text-h5">Edit Transaction</span>
          <v-btn
            small
            @click="markAsProcessed()"
            v-if="!selectedTransaction.processed"
          >
            Mark Processed
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  label="Amount"
                  required
                  v-model="editAmount"
                  type="number"
                  step="0.01"
                  :prefix="editAmount >= 0 ? '+' : ''"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  label="Description"
                  v-model="editDescription"
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-select
                  label="Category"
                  :items="categories"
                  item-text="name"
                  item-value="id"
                  v-model="editCategoryId"
                  clearable
                >
                  <template v-slot:selection="{ item }">
                    <v-chip small outlined>{{ item.name }}</v-chip>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12">
                <v-switch label="Processed" v-model="editProcessed"></v-switch>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <div class="text-caption grey--text">
                  <strong>Original Date:</strong>
                  {{ formatDate(selectedTransaction.created_at) }}
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn color="orange darken-1" text @click="openSplitDialog" small>
            Split Transaction
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="dialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="blue darken-1"
            text
            @click="saveTransaction()"
            :loading="saveLoading"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Split Transaction Dialog -->
    <v-dialog v-model="splitDialog" persistent max-width="800px">
      <v-card>
        <v-card-title class="text-h5">
          Split Transaction
          <v-spacer></v-spacer>
          <v-chip color="info" small>
            Original:
            {{
              formatAmount(
                selectedTransaction && selectedTransaction.amount
                  ? selectedTransaction.amount
                  : 0
              )
            }}
          </v-chip>
        </v-card-title>

        <v-card-text>
          <div class="text-subtitle-1 mb-3">
            <strong>{{
              selectedTransaction && selectedTransaction.description
                ? selectedTransaction.description
                : 'No description'
            }}</strong>
          </div>

          <v-row>
            <v-col cols="12">
              <v-btn
                @click="addSplitItem"
                color="primary"
                small
                outlined
                class="mb-3"
              >
                <v-icon small left>mdi-plus</v-icon>
                Add Split
              </v-btn>
            </v-col>
          </v-row>

          <div v-for="(split, index) in splitItems" :key="index" class="mb-3">
            <v-card outlined>
              <v-card-text class="pb-2">
                <v-row>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      label="Amount"
                      v-model="split.amount"
                      type="number"
                      step="0.01"
                      dense
                      outlined
                      :prefix="
                        selectedTransaction && selectedTransaction.amount >= 0
                          ? '+'
                          : '-'
                      "
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-select
                      label="Category"
                      :items="categories"
                      item-text="name"
                      item-value="id"
                      v-model="split.category_id"
                      dense
                      outlined
                      clearable
                    >
                      <template v-slot:selection="{ item }">
                        <v-chip small outlined>{{ item.name }}</v-chip>
                      </template>
                    </v-select>
                  </v-col>
                  <v-col cols="12" sm="3">
                    <v-text-field
                      label="Description"
                      v-model="split.description"
                      dense
                      outlined
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="1" class="d-flex align-center">
                    <v-btn
                      @click="removeSplitItem(index)"
                      color="error"
                      icon
                      small
                      :disabled="splitItems.length <= 1"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </div>

          <v-row class="mt-3">
            <v-col cols="12">
              <v-card color="grey lighten-4">
                <v-card-text class="py-2">
                  <div class="d-flex justify-space-between align-center">
                    <span class="text-subtitle-2">Total Split:</span>
                    <v-chip
                      :color="
                        splitTotal === originalAmount ? 'success' : 'warning'
                      "
                      small
                    >
                      {{ formatAmount(splitTotal) }}
                    </v-chip>
                  </div>
                  <div class="d-flex justify-space-between align-center mt-1">
                    <span class="text-subtitle-2">Remaining:</span>
                    <v-chip
                      :color="
                        remaining === 0
                          ? 'success'
                          : remaining < 0
                          ? 'error'
                          : 'warning'
                      "
                      small
                    >
                      {{ formatAmount(remaining) }}
                    </v-chip>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeSplitDialog">
            Cancel
          </v-btn>
          <v-btn
            color="success darken-1"
            text
            @click="saveSplitTransaction"
            :loading="splitLoading"
            :disabled="remaining !== 0"
          >
            Split & Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for notifications -->
    <Snackbar ref="snackbar" />
  </div>
</template>

<script>
import moment from 'moment';
import Snackbar from '@/components/SnackBar.vue';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, DoughnutController } from 'chart.js';

export default {
  name: 'Transactions',
  components: {
    Snackbar,
  },
  data() {
    return {
      loading: false,
      dialog: false,
      selectedTransaction: null,
      saveLoading: false,
      editAmount: null,
      editDescription: '',
      editCategoryId: null,
      editProcessed: false,
      debitViewMode: 'chips',
      creditViewMode: 'chips',
      debitChart: null,
      creditChart: null,
      selectedMonth: moment(), // Current month by default
      selectedCategoryId: null, // For filtering by category
      splitDialog: false,
      splitItems: [],
      splitLoading: false,
    };
  },
  computed: {
    transactions() {
      return this.$store.state.transactions || [];
    },
    categories() {
      return this.$store.state.categories || [];
    },
    monthlyTransactions() {
      // Since we're fetching transactions by date range (current month),
      // we just need to sort them by date (most recent first)
      // Create a copy to avoid mutating the original array
      return [...this.transactions].sort(
        (a, b) => moment(b.created_at) - moment(a.created_at)
      );
    },
    creditTransactions() {
      return this.monthlyTransactions.filter((t) => t.type === 'credit');
    },
    debitTransactions() {
      return this.monthlyTransactions.filter((t) => t.type === 'debit');
    },
    creditCategoryTotals() {
      const totals = {};
      this.creditTransactions.forEach((transaction) => {
        const categoryName =
          transaction.category && transaction.category.name
            ? transaction.category.name
            : 'Uncategorized';
        if (!totals[categoryName]) {
          totals[categoryName] = 0;
        }
        totals[categoryName] += parseFloat(transaction.amount);
      });
      return Object.entries(totals)
        .map(([name, total]) => ({
          name,
          total,
        }))
        .sort((a, b) => b.total - a.total); // Sort by total descending
    },
    debitCategoryTotals() {
      const totals = {};
      this.debitTransactions.forEach((transaction) => {
        const categoryName =
          transaction.category && transaction.category.name
            ? transaction.category.name
            : 'Uncategorized';
        if (!totals[categoryName]) {
          totals[categoryName] = 0;
        }
        totals[categoryName] += Math.abs(parseFloat(transaction.amount));
      });
      return Object.entries(totals)
        .map(([name, total]) => ({
          name,
          total,
        }))
        .sort((a, b) => b.total - a.total); // Sort by total descending
    },
    totalCredit() {
      return this.creditTransactions.reduce(
        (sum, t) => sum + parseFloat(t.amount),
        0
      );
    },
    totalDebit() {
      return Math.abs(
        this.debitTransactions.reduce((sum, t) => sum + parseFloat(t.amount), 0)
      );
    },
    displayMonthName() {
      return this.selectedMonth.format('MMMM YYYY');
    },
    isCurrentMonth() {
      return this.selectedMonth.isSame(moment(), 'month');
    },
    selectedCategoryName() {
      if (!this.selectedCategoryId) return null;
      const category = this.categories.find(
        (c) => c.id === this.selectedCategoryId
      );
      return category ? category.name : null;
    },
    originalAmount() {
      return Math.abs(
        parseFloat(
          this.selectedTransaction && this.selectedTransaction.amount
            ? this.selectedTransaction.amount
            : 0
        )
      );
    },
    splitTotal() {
      return this.splitItems.reduce((sum, item) => {
        return sum + Math.abs(parseFloat(item.amount || 0));
      }, 0);
    },
    remaining() {
      return this.originalAmount - this.splitTotal;
    },
    totalIncome() {
      // Use the same calculation as totalCredit to ensure consistency
      return this.totalCredit;
    },
    totalExpenses() {
      // Use the same calculation as totalDebit to ensure consistency
      return this.totalDebit;
    },
    netAmount() {
      return this.totalIncome - this.totalExpenses;
    },
  },
  async mounted() {
    // Register Chart.js components
    ChartJS.register(ArcElement, Tooltip, Legend, DoughnutController);

    await this.getTransactions();
    // Load categories for the select dropdown
    if (this.categories.length === 0) {
      await this.$store.dispatch('getCategories');
    }
  },
  watch: {
    debitViewMode(newMode) {
      if (newMode === 'chart') {
        this.$nextTick(() => {
          this.createDebitChart();
        });
      }
    },
    creditViewMode(newMode) {
      if (newMode === 'chart') {
        this.$nextTick(() => {
          this.createCreditChart();
        });
      }
    },
    debitCategoryTotals() {
      if (this.debitViewMode === 'chart') {
        this.$nextTick(() => {
          this.createDebitChart();
        });
      }
    },
    creditCategoryTotals() {
      if (this.creditViewMode === 'chart') {
        this.$nextTick(() => {
          this.createCreditChart();
        });
      }
    },
  },
  methods: {
    async getTransactions() {
      this.loading = true;
      try {
        // Get selected month's date range
        const startOfMonth = this.selectedMonth
          .clone()
          .startOf('month')
          .format('YYYY-MM-DD');
        const endOfMonth = this.selectedMonth
          .clone()
          .endOf('month')
          .format('YYYY-MM-DD');

        const params = {
          dateFrom: startOfMonth,
          dateTo: endOfMonth,
        };

        // Add category filter if selected
        if (this.selectedCategoryId) {
          params.category_id = this.selectedCategoryId;
        }

        await this.$store.dispatch('getTransactionsByDateRange', params);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        this.$refs.snackbar.showSnackbar(
          'Failed to load transactions',
          'error'
        );
      } finally {
        this.loading = false;
      }
    },
    viewTransaction(transaction) {
      this.selectedTransaction = transaction;
      // Pre-populate edit fields
      this.editAmount = parseFloat(transaction.amount);
      this.editDescription = transaction.description || '';
      this.editCategoryId = transaction.category
        ? transaction.category.id
        : null;
      this.editProcessed =
        transaction.processed == 1 || transaction.processed === true;
      this.dialog = true;
    },
    async saveTransaction() {
      this.saveLoading = true;
      try {
        const updateData = {
          amount: parseFloat(this.editAmount),
          description: this.editDescription,
          category_id: this.editCategoryId,
          processed: this.editProcessed ? 1 : 0,
        };

        await this.$store.dispatch('processTransaction', {
          id: this.selectedTransaction.id,
          data: updateData,
        });

        this.dialog = false;
        this.$refs.snackbar.showSnackbar(
          'Transaction updated successfully',
          'success'
        );
        // Refresh transactions
        await this.getTransactions();
      } catch (error) {
        console.error('Error updating transaction:', error);
        this.$refs.snackbar.showSnackbar(
          'Failed to update transaction',
          'error'
        );
      } finally {
        this.saveLoading = false;
      }
    },
    markAsProcessed() {
      this.editProcessed = true;
    },
    formatDate(date) {
      return moment(date).format('MMM DD, YYYY');
    },
    formatAmount(amount) {
      const num = parseFloat(amount);
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(num);
    },
    getAmountColor(amount) {
      return parseFloat(amount) >= 0 ? 'green--text' : 'red--text';
    },
    getProcessedText(processed) {
      return processed == 1 || processed === true ? 'Yes' : 'No';
    },
    getProcessedColor(processed) {
      return processed == 1 || processed === true ? 'success' : 'warning';
    },
    getProcessedTextColor() {
      return 'white';
    },
    getCategoryColor(categoryName) {
      // Generate a consistent color for each category based on its name
      const colors = [
        'primary',
        'secondary',
        'accent',
        'info',
        'warning',
        'error',
        'purple',
        'pink',
        'indigo',
        'cyan',
        'teal',
        'green',
        'orange',
        'deep-orange',
        'brown',
        'blue-grey',
      ];

      // Simple hash function to get consistent color for same category
      let hash = 0;
      for (let i = 0; i < categoryName.length; i++) {
        hash = categoryName.charCodeAt(i) + ((hash << 5) - hash);
      }

      return colors[Math.abs(hash) % colors.length];
    },
    createDebitChart() {
      if (this.debitChart) {
        this.debitChart.destroy();
      }

      const ctx =
        this.$refs.debitChart && this.$refs.debitChart.getContext('2d');
      if (!ctx || this.debitCategoryTotals.length === 0) return;

      const data = {
        labels: this.debitCategoryTotals.map((item) => item.name),
        datasets: [
          {
            data: this.debitCategoryTotals.map((item) => item.total),
            backgroundColor: this.debitCategoryTotals.map((item) =>
              this.getChartColor(this.getCategoryColor(item.name))
            ),
            borderWidth: 1,
          },
        ],
      };

      this.debitChart = new ChartJS(ctx, {
        type: 'doughnut',
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = this.formatAmount(context.parsed);
                  return `${label}: ${value}`;
                },
              },
            },
          },
        },
      });
    },
    createCreditChart() {
      if (this.creditChart) {
        this.creditChart.destroy();
      }

      const ctx =
        this.$refs.creditChart && this.$refs.creditChart.getContext('2d');
      if (!ctx || this.creditCategoryTotals.length === 0) return;

      const data = {
        labels: this.creditCategoryTotals.map((item) => item.name),
        datasets: [
          {
            data: this.creditCategoryTotals.map((item) => item.total),
            backgroundColor: this.creditCategoryTotals.map((item) =>
              this.getChartColor(this.getCategoryColor(item.name))
            ),
            borderWidth: 1,
          },
        ],
      };

      this.creditChart = new ChartJS(ctx, {
        type: 'doughnut',
        data: data,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const label = context.label || '';
                  const value = this.formatAmount(context.parsed);
                  return `${label}: ${value}`;
                },
              },
            },
          },
        },
      });
    },
    getChartColor(vuetifyColor) {
      // Convert Vuetify color names to hex colors for Chart.js
      const colorMap = {
        primary: '#1976D2',
        secondary: '#424242',
        accent: '#82B1FF',
        info: '#2196F3',
        warning: '#FF9800',
        error: '#F44336',
        success: '#4CAF50',
        purple: '#9C27B0',
        pink: '#E91E63',
        indigo: '#3F51B5',
        cyan: '#00BCD4',
        teal: '#009688',
        green: '#4CAF50',
        orange: '#FF9800',
        'deep-orange': '#FF5722',
        brown: '#795548',
        'blue-grey': '#607D8B',
      };
      return colorMap[vuetifyColor] || '#90A4AE';
    },
    goToPreviousMonth() {
      this.selectedMonth = this.selectedMonth.clone().subtract(1, 'month');
      this.getTransactions();
    },
    goToNextMonth() {
      if (!this.isCurrentMonth) {
        this.selectedMonth = this.selectedMonth.clone().add(1, 'month');
        this.getTransactions();
      }
    },
    goToCurrentMonth() {
      this.selectedMonth = moment();
      this.getTransactions();
    },
    filterByCategory(categoryName) {
      // Find the category ID by name
      const category = this.categories.find((c) => c.name === categoryName);
      if (category) {
        this.selectedCategoryId = category.id;
        this.getTransactions();
      }
    },
    clearFilter() {
      this.selectedCategoryId = null;
      this.getTransactions();
    },
    openSplitDialog() {
      this.splitDialog = true;
      this.dialog = false; // Close the edit dialog
      this.initializeSplitItems();
    },
    closeSplitDialog() {
      this.splitDialog = false;
      this.splitItems = [];
      this.dialog = true; // Reopen the edit dialog
    },
    initializeSplitItems() {
      // Initialize with two empty splits for the user to fill in
      this.splitItems = [
        {
          amount: 0,
          category_id:
            this.selectedTransaction &&
            this.selectedTransaction.category &&
            this.selectedTransaction.category.id
              ? this.selectedTransaction.category.id
              : null,
          description:
            this.selectedTransaction && this.selectedTransaction.description
              ? this.selectedTransaction.description
              : '',
        },
        {
          amount: 0,
          category_id: null,
          description: '',
        },
      ];
    },
    addSplitItem() {
      this.splitItems.push({
        amount: 0,
        category_id: null,
        description: '',
      });
    },
    removeSplitItem(index) {
      if (this.splitItems.length > 1) {
        this.splitItems.splice(index, 1);
      }
    },
    async saveSplitTransaction() {
      this.splitLoading = true;
      try {
        // Validate that amounts add up correctly
        if (this.remaining !== 0) {
          this.$refs.snackbar.showSnackbar(
            'Split amounts must equal the original transaction amount',
            'error'
          );
          return;
        }

        // Validate that all splits have categories and amounts
        for (let split of this.splitItems) {
          if (!split.amount || split.amount <= 0) {
            this.$refs.snackbar.showSnackbar(
              'All split amounts must be greater than 0',
              'error'
            );
            return;
          }
          if (!split.category_id) {
            this.$refs.snackbar.showSnackbar(
              'All splits must have a category selected',
              'error'
            );
            return;
          }
        }

        // First split: Update the original transaction
        const [firstSplit, ...additionalSplits] = this.splitItems;

        console.log('First split data:', firstSplit);
        console.log(
          'Original transaction amount:',
          this.selectedTransaction.amount
        );
        console.log('First split amount:', firstSplit.amount);

        await this.$store.dispatch('processTransaction', {
          id: this.selectedTransaction.id,
          data: {
            amount:
              this.selectedTransaction.amount < 0
                ? -Math.abs(parseFloat(firstSplit.amount))
                : Math.abs(parseFloat(firstSplit.amount)),
            category_id: firstSplit.category_id,
            description:
              firstSplit.description || this.selectedTransaction.description,
            processed: 1,
          },
        });

        // Additional splits: Create new transactions
        if (additionalSplits.length > 0) {
          const createPromises = additionalSplits.map((split) => {
            return this.$store.dispatch('createTransaction', {
              amount:
                this.selectedTransaction.amount < 0
                  ? -Math.abs(parseFloat(split.amount))
                  : Math.abs(parseFloat(split.amount)),
              category_id: split.category_id,
              description:
                split.description || this.selectedTransaction.description,
              processed: 1,
              type: this.selectedTransaction.type,
              gmail_id:
                this.selectedTransaction.gmail_id + '-' + split.amount || null,
            });
          });

          await Promise.all(createPromises);
        }

        this.splitDialog = false;
        this.dialog = false;
        this.$refs.snackbar.showSnackbar(
          'Transaction split successfully',
          'success'
        );

        // Refresh transactions
        await this.getTransactions();
      } catch (error) {
        console.error('Error splitting transaction:', error);
        this.$refs.snackbar.showSnackbar(
          'Failed to split transaction',
          'error'
        );
      } finally {
        this.splitLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.chart-container {
  position: relative;
  height: 300px;
  margin: 20px 0;
}

.chart-container canvas {
  max-height: 300px;
}
</style>
